import { Droppable } from '@hello-pangea/dnd';
import {
    Chart,
    DocumentText,
    Grid1,
    Image as ImageIcon,
    SeparatorHorizontal,
    type IconComponent,
} from '@/components/icons';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import DraggableToolboxItem from './DraggableToolboxItem';
import type { PanelScope } from '@/pages/create-report/context/types';

interface ToolboxItem {
    id: string;
    label: string;
    icon: IconComponent;
    tooltip: string;
    dragOnly?: boolean;
}

/** The half of the tooltip that is the same for every tool that takes a click. */
const PLACEMENT_HINT = 'Arraste para escolher a posição ou clique para inserir no fim do grupo.';

/**
 * The divider leads and is the only drag-only tool: it closes the run it lands
 * in, so its position *is* its content and "insert at the end of the group"
 * would never mean anything. `tool_image` is behind `reportImages.blocks` and
 * is filtered out — not appended — so the order below is the order on screen.
 *
 * Each tooltip says what the block is before it says how to place it: the
 * label already names the block, and a tooltip that only repeats the label
 * costs a hover for nothing.
 */
const TOOLBOX_ITEMS: ToolboxItem[] = [
    {
        id: 'tool_divider',
        label: 'Divisor de seção',
        icon: SeparatorHorizontal,
        tooltip:
            'Abre uma nova seção do laudo, como inteligência ou memória. Arraste até o ponto onde ela começa; a categoria você escolhe depois.',
        dragOnly: true,
    },
    {
        id: 'tool_text',
        label: 'Texto livre / interpretação',
        icon: DocumentText,
        tooltip: `Um texto livre, normalmente a interpretação dos escores. ${PLACEMENT_HINT}`,
    },
    {
        id: 'tool_chart',
        label: 'Gráfico',
        icon: Chart,
        tooltip: `Um gráfico dos escores, montado no próprio laudo. ${PLACEMENT_HINT}`,
    },
    {
        id: 'tool_image',
        label: 'Imagem',
        icon: ImageIcon,
        tooltip: `Uma imagem sua, enviada depois que o bloco entra no laudo. ${PLACEMENT_HINT}`,
    },
    {
        id: 'tool_table',
        label: 'Adicionar outra tabela',
        icon: Grid1,
        tooltip: `Uma tabela em branco, para escores ou dados que você mesmo digita. ${PLACEMENT_HINT}`,
    },
];

interface BlockToolboxPanelProps {
    scope: PanelScope;
    onAddBlock?: (toolId: string, scope?: { anchorRealId: string | null }) => void;
    /** Tools the panel above already offers. The actions half of a scoped
     *  instrument carries "Gerar gráfico deste instrumento", which lands the
     *  exact same block as the `tool_chart` tile — two entries for one thing,
     *  one of them named twice as well. The caller says which to drop rather
     *  than this list guessing what is on screen above it. */
    hiddenToolIds?: string[];
}

/**
 * The tools panel's inner level: the blocks that go *with* something already in
 * the report. It carries no title of its own — the panel header's title
 * already names the group, and repeating it here just spent two lines on one
 * piece of information.
 */
export default function BlockToolboxPanel({
    scope,
    onAddBlock,
    hiddenToolIds,
}: BlockToolboxPanelProps) {
    const reportImagesFeature = useFeatureFlag('reportImages.blocks');
    const items = TOOLBOX_ITEMS.filter((item) => {
        if (item.id === 'tool_image' && !reportImagesFeature.enabled) return false;
        return !hiddenToolIds?.includes(item.id);
    });

    return (
        // The droppable id stays `toolbox`: only one panel level is mounted at a
        // time, and `useReportDragAndDrop` keys the toolbox branch off it.
        <Droppable droppableId="toolbox" isDropDisabled={true}>
            {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                    {/* Same 200ms the rest of the report chrome waits before it
                        shows itself, so a pointer crossing the grid on its way
                        somewhere else never trails tooltips behind it.
                        `disableHoverableContent` because these tooltips are
                        passive text: without it Radix holds the open one alive
                        over the corridor between card and bubble, and the card
                        under that corridor never gets its own. */}
                    <TooltipProvider delayDuration={200} disableHoverableContent>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
                            {items.map((item, index) => (
                                <DraggableToolboxItem
                                    key={item.id}
                                    id={item.id}
                                    index={index}
                                    icon={item.icon}
                                    label={item.label}
                                    tooltip={item.tooltip}
                                    dragOnly={item.dragOnly}
                                    onClick={
                                        item.dragOnly
                                            ? undefined
                                            : () =>
                                                  onAddBlock?.(item.id, {
                                                      anchorRealId: scope.anchorRealId,
                                                  })
                                    }
                                    className="h-full p-3"
                                />
                            ))}
                        </div>
                    </TooltipProvider>

                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
}
